import { useMutation } from '@tanstack/react-query';
import { useApiClient } from './useApiClient.ts';
import type { RefreshableMetric } from '../components/admin-refresh/registry.ts';
import { buildRefreshUrl } from '../components/admin-refresh/registry.ts';

export type RefreshResult =
  | { kind: 'ok'; body: unknown }
  | { kind: 'may_still_be_running' }
  | { kind: 'forbidden' }
  | { kind: 'not_found' }
  | { kind: 'validation_error'; message: string }
  | { kind: 'generic_failure'; message: string };

interface MutateInput {
  metric: RefreshableMetric;
  params: Record<string, string>;
}

function classifyError(err: unknown): RefreshResult {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return { kind: 'may_still_be_running' };
  }
  const status = (err as { status?: number }).status;
  if (status === 502 || status === 503 || status === 504) {
    return { kind: 'may_still_be_running' };
  }
  if (status === 403) return { kind: 'forbidden' };
  // apiClient has already signed the user out on a 401; report it as a failure.
  if (status === 401) {
    return { kind: 'generic_failure', message: (err as Error).message };
  }
  if (status === 404) return { kind: 'not_found' };
  if (status && status >= 400 && status < 500) {
    const body = (err as { body?: { message?: string } }).body;
    return {
      kind: 'validation_error',
      message: body?.message ?? (err as Error).message,
    };
  }
  return {
    kind: 'generic_failure',
    message: (err as Error).message ?? 'Unknown error',
  };
}

export function useAdminRefresh() {
  const apiClient = useApiClient();
  return useMutation<RefreshResult, never, MutateInput>({
    mutationFn: async ({ metric, params }) => {
      const url = buildRefreshUrl(metric, params);
      const signal = AbortSignal.timeout(60_000);
      try {
        const body = await apiClient.post<unknown>(url, undefined, { signal });
        return { kind: 'ok', body };
      } catch (err) {
        return classifyError(err);
      }
    },
  });
}
